'use client';

import { FC } from 'react';
import { Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { COLORS } from '@/constants';
import { IHousingObjectResponse } from '@/types';
import { StatItem, StatsWrapper } from './styled';
import { formatRange, formatPriceRange } from '@/features/pages/project-detail/utils';
import { OfferType } from '@/types';

type ProjectCardStatsProps = {
  data: IHousingObjectResponse;
  offerType?: OfferType | string;
  inline?: boolean;
};

export const ProjectCardStats: FC<ProjectCardStatsProps> = ({ data, offerType, inline }) => {
  const t = useTranslations();
  const pd = data.projectData;

  const isRent = offerType === OfferType.RENT;
  const priceRange = isRent
    ? (formatPriceRange(pd?.rentFrom, pd?.rentTo) ??
      formatPriceRange(pd?.priceFrom, pd?.priceTo) ??
      '—')
    : (formatPriceRange(pd?.priceFrom, pd?.priceTo) ??
      formatPriceRange(pd?.rentFrom, pd?.rentTo) ??
      '—');

  const areaRange = formatRange(pd?.areaFrom, pd?.areaTo, 'm²') ?? '—';
  const roomRangeRaw = formatRange(pd?.roomFrom, pd?.roomTo);
  const roomRange = roomRangeRaw ? `${roomRangeRaw}\u00A0${t('shortRooms')}` : '—';

  const priceLabel = isRent
    ? t('housingCardDetails.rentPriceLabel')
    : t('housingCardDetails.sellPriceLabel');

  return (
    <StatsWrapper $inline={inline}>
      <StatItem>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {priceLabel}
        </Typography>
        <Typography
          fontSize={16}
          lineHeight={1.26}
          fontWeight={600}
          color={COLORS['gray-dark-2']}
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {priceRange}
        </Typography>
      </StatItem>
      <StatItem>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {t('searchResult.area')}
        </Typography>
        <Typography
          fontSize={16}
          lineHeight={1.26}
          fontWeight={600}
          color={COLORS['gray-dark-2']}
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {areaRange}
        </Typography>
      </StatItem>
      <StatItem $break>
        <Typography fontSize={12} color={COLORS['neutral/500']}>
          {t('searchResult.rooms')}
        </Typography>
        <Typography fontSize={16} lineHeight={1.26} fontWeight={600} color={COLORS['gray-dark-2']}>
          {roomRange}
        </Typography>
      </StatItem>
    </StatsWrapper>
  );
};
